<div class="main-view space-y-2 md:space-y-4">
    <div class="flex flex-wrap items-start gap-2">
        <div class="min-w-0 flex-1 space-y-1">
            <app-page-surface-header titleIcon="user"
                [compact]="true"
                [title]="user?.full_name || 'User details'"
                subtitle="Portal user profile and agents created by this user."
            />
            <p *ngIf="user?.unique_id" class="text-xs text-gray-500 dark:text-gray-400">
                Reference ID: <span class="font-mono">{{ user!.unique_id }}</span>
            </p>
        </div>
        <a
            *ngIf="user && !isSuperAdminUser && editUserLink() as editLink"
            [routerLink]="editLink"
            class="btn inline-flex shrink-0 items-center gap-2 rounded-full border border-gray-300 bg-white px-4 py-2 text-sm font-medium dark:border-[#191e3a] dark:bg-[#0e1726] dark:text-white"
        >
            <icon-edit class="size-4" />
            Edit user
        </a>
    </div>

    <div *ngIf="loadError" class="rounded-xl border border-red-200 bg-red-50 px-4 py-6 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200">
        {{ loadError }}
        <div class="mt-4">
            <a routerLink="/manage-admin-users" class="btn inline-flex rounded-full bg-white px-4 py-2 text-black">Back to users</a>
        </div>
    </div>

    <div *ngIf="loading && !loadError" class="rounded-3xl border border-gray-200 bg-white/60 p-8 dark:border-[#191e3a] dark:bg-[#0e1726]">
        <p class="text-sm text-gray-500">Loading user…</p>
    </div>

    <ng-container *ngIf="user && !loadError">
        <div class="rounded-3xl border border-gray-200 bg-white/60 p-6 shadow-sm dark:border-[#191e3a] dark:bg-[#0e1726] md:p-8">
            <div class="flex flex-wrap items-start gap-4">
                <div *ngIf="profileImageUrl()" class="h-16 w-16 shrink-0 overflow-hidden rounded-2xl border border-gray-200 dark:border-[#191e3a]">
                    <img [src]="profileImageUrl()!" class="h-full w-full object-cover" alt="" />
                </div>
                <div
                    *ngIf="!profileImageUrl()"
                    class="grid h-16 w-16 shrink-0 place-content-center rounded-2xl bg-primary text-xl font-semibold text-white"
                >
                    {{ initials(user.full_name) }}
                </div>
                <div class="min-w-0 flex-1">
                    <div class="flex flex-wrap items-center gap-2">
                        <h2 class="text-xl font-bold text-gray-900 dark:text-white">{{ user.full_name }}</h2>
                        <span
                            class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-bold"
                            [ngClass]="statusBadgeClass(user.status)"
                        >
                            {{ user.status === 'InActive' ? 'Inactive' : user.status }}
                        </span>
                    </div>
                    <p *ngIf="roleLabel()" class="mt-0.5 text-sm text-gray-500 dark:text-gray-400">{{ roleLabel() }}</p>
                </div>
            </div>

            <div class="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <div class="rounded-xl bg-gray-50 px-4 py-3 dark:bg-white/[0.04]">
                    <p class="text-xs font-medium text-gray-500 dark:text-gray-400">Email</p>
                    <p class="mt-1 truncate text-sm font-medium text-gray-900 dark:text-white">{{ user.email_id || '—' }}</p>
                </div>
                <div class="rounded-xl bg-gray-50 px-4 py-3 dark:bg-white/[0.04]">
                    <p class="text-xs font-medium text-gray-500 dark:text-gray-400">Phone</p>
                    <p class="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                        {{ user.mobile_number | tenantPhone : user.country_code : user.country_code }}
                    </p>
                </div>
                <div class="rounded-xl bg-gray-50 px-4 py-3 dark:bg-white/[0.04]">
                    <p class="text-xs font-medium text-gray-500 dark:text-gray-400">Created</p>
                    <p class="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                        {{ user.created_at ? (user.created_at | appDate) : '—' }}
                    </p>
                </div>
            </div>
        </div>

        <app-page-body-panel>
            <div ngProjectAs="[pageToolbar]">
                <div>
                    <h3 class="text-base font-bold text-gray-900 dark:text-white">Agents created</h3>
                    <p class="text-xs text-gray-500 dark:text-gray-400">
                        {{ agentsTotal }} agent{{ agentsTotal === 1 ? '' : 's' }} created by this user
                    </p>
                </div>
            </div>

            <div class="table-responsive mt-1">
                <table class="table table-hover table-surface">
                    <thead>
                        <tr>
                            <th>Code</th>
                            <th>Agent</th>
                            <th>Product</th>
                            <th>Client</th>
                            <th>Status</th>
                            <th class="text-end">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr *ngIf="agentsLoading">
                            <td colspan="6" class="py-10 text-center text-sm text-gray-500">Loading agents…</td>
                        </tr>
                        <tr *ngIf="!agentsLoading && !agents.length">
                            <td colspan="6" class="py-10 text-center text-sm text-gray-500">
                                This user has not created any agents yet.
                            </td>
                        </tr>
                        <tr *ngFor="let agent of agents" class="border-none">
                            <td class="font-mono text-xs">{{ agent.agent_code || '—' }}</td>
                            <td class="font-medium text-primary dark:text-white">{{ agent.name }}</td>
                            <td>{{ productName(agent) }}</td>
                            <td>{{ clientName(agent) }}</td>
                            <td>
                                <span
                                    class="inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold"
                                    [ngClass]="agentStatusBadgeClass(agent.status)"
                                >
                                    {{ agent.status }}
                                </span>
                            </td>
                            <td class="text-end">
                                <a
                                    [routerLink]="['/create-agent/agent-details']"
                                    [queryParams]="{ agent: agent.uuid }"
                                    class="text-xs font-semibold text-primary hover:underline"
                                >
                                    Open
                                </a>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </app-page-body-panel>

        <app-page-body-panel>
            <div ngProjectAs="[pageToolbar]" class="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h3 class="text-base font-bold text-gray-900 dark:text-white">Permissions</h3>
                    <p class="text-xs text-gray-500 dark:text-gray-400">
                        <ng-container *ngIf="isSuperAdminUser">Super Admin role — full portal access</ng-container>
                        <ng-container *ngIf="!isSuperAdminUser && roleLabel()">
                            Via role: <span class="font-medium text-gray-700 dark:text-gray-300">{{ roleLabel() }}</span>
                            <ng-container *ngIf="permissionsTotal > 0"> · {{ permissionsTotal }} permission{{ permissionsTotal === 1 ? '' : 's' }}</ng-container>
                        </ng-container>
                        <ng-container *ngIf="!isSuperAdminUser && !roleLabel()">No role assigned</ng-container>
                    </p>
                </div>
                <div class="flex flex-wrap items-center gap-2">
                    @if (permissionGroups.length > 1) {
                    <button
                        type="button"
                        class="btn inline-flex shrink-0 items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-none hover:bg-gray-50 dark:border-[#191e3a] dark:bg-[#0e1726] dark:text-white dark:hover:bg-white/5"
                        (click)="toggleAllPermissionGroups()"
                    >
                        <icon-carets-down
                            class="size-4 transition-transform"
                            [class.-rotate-90]="!allPermissionGroupsExpanded"
                        />
                        {{ allPermissionGroupsExpanded ? 'Collapse all' : 'Expand all' }}
                    </button>
                    }
                    @if (!isSuperAdminUser && roleId() && ((P.ROLE_MANAGE_PERMISSIONS | hasPermission) || (roleAttachSlugs | hasPermission))) {
                    <button
                        type="button"
                        class="btn inline-flex shrink-0 items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-none hover:bg-gray-50 dark:border-[#191e3a] dark:bg-[#0e1726] dark:text-white dark:hover:bg-white/5"
                        (click)="goToManageRolePermissions()"
                    >
                        <icon-settings class="size-4" />
                        Manage role permissions
                    </button>
                    }
                </div>
            </div>

            <div *ngIf="isSuperAdminUser" class="rounded-xl border border-primary/20 bg-primary/5 px-4 py-4 text-sm text-gray-700 dark:border-primary/30 dark:bg-primary/10 dark:text-gray-200">
                This user has the Super Admin role and can access all features in the portal.
            </div>

            <div *ngIf="!isSuperAdminUser && !permissionGroups.length" class="py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                <ng-container *ngIf="roleLabel(); else noRole">No permissions are assigned to this user's role yet.</ng-container>
                <ng-template #noRole>Assign a role to grant permissions.</ng-template>
            </div>

            <div *ngIf="!isSuperAdminUser && permissionGroups.length" class="space-y-2">
                <div
                    *ngFor="let group of permissionGroups"
                    class="overflow-hidden rounded-xl border border-gray-100 dark:border-[#191e3a]"
                >
                    <button
                        type="button"
                        class="flex w-full items-center gap-3 border-b border-gray-100 bg-gray-50 px-4 py-3 text-left transition-colors hover:bg-gray-100 dark:border-[#191e3a] dark:bg-[#0e1726] dark:hover:bg-[#1a2332]"
                        [attr.aria-expanded]="group.expanded"
                        (click)="togglePermissionGroup(group)"
                    >
                        <icon-caret-down
                            class="size-4 shrink-0 text-gray-500 transition-transform duration-200"
                            [class.-rotate-90]="!group.expanded"
                        />
                        <span class="min-w-0 flex-1 text-sm font-semibold text-gray-800 dark:text-white">{{ group.module }}</span>
                        <span class="shrink-0 rounded-full bg-gray-200/80 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-white/10 dark:text-gray-400">
                            {{ group.permissions.length }}
                        </span>
                    </button>
                    <ul
                        *ngIf="group.expanded"
                        class="grid grid-cols-1 gap-2 border-t border-gray-100 p-4 sm:grid-cols-2 lg:grid-cols-3 dark:border-[#191e3a]"
                    >
                        <li
                            *ngFor="let perm of group.permissions"
                            class="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300"
                        >
                            <icon-circle-check class="mt-0.5 size-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
                            <span>
                                <span class="font-medium">{{ permissionDisplayName(perm) }}</span>
                                <span
                                    *ngIf="perm.slug && perm.slug !== permissionDisplayName(perm)"
                                    class="mt-0.5 block font-mono text-xs text-gray-400"
                                >{{ perm.slug }}</span>
                            </span>
                        </li>
                    </ul>
                </div>
            </div>
        </app-page-body-panel>
        <app-audit-history *ngIf="user" resourceType="user" [resourceId]="user.id" class="mt-5 block"></app-audit-history>
    </ng-container>
</div>
